import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

type IdeaType = 'product' | 'dienst' | 'app';
type TierId = 'starter' | 'pro';

interface ProcessStep {
  icon: string;
  title: string;
  description: string;
}

const processSteps: ProcessStep[] = [
  {
    icon: '📝',
    title: 'Aanvraag',
    description: 'Vul je concept in. Wij nemen binnen 1 werkdag contact op voor betaling.',
  },
  {
    icon: '🎨',
    title: 'Design & akkoord',
    description: 'Wij leveren een design. Jij keurt het goed, daarna pas bouwen we.',
  },
  {
    icon: '📊',
    title: 'Live + intentie meten',
    description: 'Ads draaien. Op het cruciale moment zien bezoekers een blokkade, zo meten we echte intentie.',
  },
  {
    icon: '📄',
    title: 'Rapport',
    description: 'Jij ontvangt alle data. Doorstarten of niet, jij beslist.',
  },
];

interface TierConfig {
  id: TierId;
  name: string;
  price: number;
  popular?: boolean;
  features: string[];
}

const tiers: TierConfig[] = [
  {
    id: 'starter',
    name: 'Starter',
    price: 295,
    features: [
      'MVP website (mobile-first design)',
      '1 Google Ads campagne setup',
      'Advertentiebudget €70 inbegrepen (€10 per dag)',
      '1 week live',
      'Meting van klikken en interesse-signalen',
      'PDF-rapport met kerncijfers',
      'Bij doorstart: €95 korting op je vervolgpakket',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 395,
    popular: true,
    features: [
      'Alles van Starter, plus:',
      '2 verschillende pitches getest (welke werkt het beste?)',
      'Advertentiebudget €140 inbegrepen (€10 per dag)',
      '2 weken live (meer data, betrouwbaarder)',
      'Uitgebreid rapport met concrete aanbevelingen',
      '30-min strategy call om resultaten te bespreken',
      'Bij doorstart: €175 korting op je vervolgpakket',
    ],
  },
];

interface FAQItem {
  question: string;
  answer: string;
}

const faq: FAQItem[] = [
  {
    question: 'Wat gebeurt er als mijn idee succesvol blijkt?',
    answer:
      'Dan kun je doorstarten en bij ons de volledige website of app laten bouwen. Je krijgt automatisch korting op je vervolgpakket (€95 bij Starter, €175 bij Pro). De MVP website en het opgebouwde inzicht zijn dan jouw startpunt.',
  },
  {
    question: 'Hoe meten jullie precies of mensen jouw idee willen kopen?',
    answer:
      'We meten elke klik op de "koop" of "aanmelden" knop. Op dat moment zien bezoekers geen betaalpagina, maar een melding dat ze een notificatie krijgen zodra je product of dienst beschikbaar is. Iedere klik telt als sterk interesse-signaal en je kan zelfs een e-maillijst opbouwen van potentiële klanten.',
  },
  {
    question: 'Is dit niet misleidend voor de bezoekers?',
    answer:
      'Nee, we vragen nooit betaalgegevens. Bezoekers krijgen een eerlijke "notificatie zodra beschikbaar" melding, geen "uitverkocht"-truc. Dat is een veelgebruikte en juridisch nette manier om interesse te peilen voor producten of diensten die nog niet bestaan.',
  },
  {
    question: 'Wat als ik niet wil doorstarten?',
    answer:
      'Geen probleem. Je krijgt het rapport en de inzichten ongeacht je keuze. Je hebt vanaf €295 geleerd of je idee marktwaardig is, in plaats van duizenden euro\'s te investeren in iets dat niet werkt.',
  },
];

const IdeeTester: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Idee Tester');

  const [ideaType, setIdeaType] = useState<IdeaType>('product');
  const [ideaTitle, setIdeaTitle] = useState('');
  const [ideaDescription, setIdeaDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [selectedTier, setSelectedTier] = useState<TierId>('starter');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    try {
      const response = await fetch('/.netlify/functions/submit-idee-tester', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ideaType,
          ideaTitle,
          ideaDescription,
          keywords,
          selectedTier,
          contact: { name, email, phone },
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Er ging iets mis');
      }

      setSubmitStatus('success');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmitStatus('error');
      setErrorMessage(err.message || 'Er ging iets mis, probeer het opnieuw');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitStatus === 'success') {
    return (
      <div className="min-h-screen">
        <div
          className="relative py-20"
          style={{
            backgroundImage: `url('/hero-background.webp')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="container mx-auto px-8">
            <h1 className="text-5xl font-bold text-white mb-4">Bedankt!</h1>
            <p className="text-xl text-white/90 max-w-3xl">
              We hebben je aanvraag ontvangen en nemen binnen 1 werkdag contact met je op om te starten.
            </p>
          </div>
        </div>
        <div className="container mx-auto px-8 py-16 text-center">
          <button
            onClick={() => navigate('/')}
            className="text-white px-8 py-3 rounded-lg font-bold transition transform hover:scale-105 shadow-lg"
            style={{
              background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
            }}
          >
            Terug naar Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
            Test of jouw idee werkt, voordat je er geld in steekt
          </h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Voor €295 weet je binnen 1 week of jouw idee klanten heeft, met echte data uit Google Ads.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-8 py-16">
        <div className="max-w-5xl mx-auto">
          {/* Process Steps */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Zo werkt het</h2>
            <ol className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {processSteps.map((step, index) => (
                <li
                  key={step.title}
                  className="bg-white rounded-xl p-5 shadow-md border border-gray-100 relative"
                >
                  <div className="flex items-center mb-3">
                    <div
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold shadow-md mr-3"
                      style={{
                        background:
                          'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                      }}
                    >
                      {index + 1}
                    </div>
                    <span className="text-2xl">{step.icon}</span>
                  </div>
                  <h3 className="font-bold text-blue-900 mb-1">{step.title}</h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{step.description}</p>
                </li>
              ))}
            </ol>
          </section>

          {/* Pricing Tiers */}
          <section className="mb-16">
            <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Kies je pakket</h2>
            <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
              {tiers.map((tier) => {
                const isSelected = selectedTier === tier.id;
                return (
                  <div
                    key={tier.id}
                    onClick={() => setSelectedTier(tier.id)}
                    className={`relative cursor-pointer rounded-2xl p-6 transition transform hover:-translate-y-1 ${
                      isSelected
                        ? 'bg-white shadow-xl border-2'
                        : 'bg-white/95 shadow-md border-2 border-gray-100 hover:shadow-lg'
                    }`}
                    style={isSelected ? { borderColor: '#f06c28' } : {}}
                  >
                    {tier.popular && (
                      <div
                        className="absolute -top-3 right-6 text-white text-xs font-bold px-3 py-1 rounded-full"
                        style={{
                          background:
                            'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                        }}
                      >
                        Meest gekozen
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-blue-900 mb-2">{tier.name}</h3>
                    <div className="text-4xl font-bold text-blue-900 mb-4">
                      €{tier.price}
                      <span className="text-base text-gray-500 font-normal">,-</span>
                    </div>
                    <ul className="space-y-2 mb-4">
                      {tier.features.map((feature) => (
                        <li key={feature} className="flex items-start text-sm text-gray-700">
                          <span className="text-green-500 mr-2 flex-shrink-0">✓</span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                    {isSelected && (
                      <div className="text-center text-sm font-semibold text-orange-600 mt-4">
                        Geselecteerd ✓
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {/* Form */}
          <section className="mb-16">
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8">
              <div className="flex flex-wrap items-baseline justify-between gap-3 mb-6">
                <h2 className="text-3xl font-bold text-blue-900">Jouw aanvraag</h2>
                <button
                  type="button"
                  onClick={() => document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' })}
                  className="text-sm font-semibold text-orange-600 hover:text-orange-700 hover:underline transition"
                >
                  Eerst nog vragen? Bekijk de FAQ ↓
                </button>
              </div>

              {submitStatus === 'error' && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700">
                  {errorMessage}
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Wat voor idee?
                  </label>
                  <div className="flex flex-wrap gap-3">
                    {(['product', 'dienst', 'app'] as IdeaType[]).map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => setIdeaType(type)}
                        className={`px-5 py-2 rounded-lg font-medium capitalize transition ${
                          ideaType === type
                            ? 'text-white shadow-md'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                        style={
                          ideaType === type
                            ? {
                                background:
                                  'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                              }
                            : {}
                        }
                      >
                        {type === 'product' && '📦 Product'}
                        {type === 'dienst' && '🤝 Dienst'}
                        {type === 'app' && '📱 App'}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label htmlFor="ideaTitle" className="block text-sm font-semibold text-gray-700 mb-2">
                    Jouw idee in één zin <span className="text-red-500">*</span>
                  </label>
                  <input
                    id="ideaTitle"
                    type="text"
                    required
                    value={ideaTitle}
                    onChange={(e) => setIdeaTitle(e.target.value)}
                    placeholder="Bijv. Biologische lunch-bezorging op kantoor"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition"
                  />
                </div>

                <div>
                  <label htmlFor="ideaDescription" className="block text-sm font-semibold text-gray-700 mb-2">
                    Omschrijving <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <textarea
                    id="ideaDescription"
                    rows={4}
                    value={ideaDescription}
                    onChange={(e) => setIdeaDescription(e.target.value)}
                    placeholder="Wie is je doelgroep? Welk probleem los je op? Wat is je voorgestelde prijs?"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition resize-none"
                  />
                </div>

                <div>
                  <label htmlFor="keywords" className="block text-sm font-semibold text-gray-700 mb-2">
                    Keywords voor de ads campagne <span className="text-gray-400 font-normal">(optioneel)</span>
                  </label>
                  <input
                    id="keywords"
                    type="text"
                    value={keywords}
                    onChange={(e) => setKeywords(e.target.value)}
                    placeholder="Bijv. biologische lunch, kantoor catering, gezonde lunch bezorging"
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Komma's gebruiken om meerdere zoekwoorden te scheiden. Geen idee? Laat leeg, wij denken mee.
                  </p>
                </div>

                <div className="border-t pt-6">
                  <h3 className="text-lg font-bold text-blue-900 mb-4">Contactgegevens</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2">
                        Naam <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="name"
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                        E-mail <span className="text-red-500">*</span>
                      </label>
                      <input
                        id="email"
                        type="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                      Telefoon
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-200 transition"
                    />
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-600">Gekozen pakket:</div>
                    <div className="font-bold text-blue-900">
                      {tiers.find((t) => t.id === selectedTier)?.name}, €
                      {tiers.find((t) => t.id === selectedTier)?.price},-
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full text-white py-4 rounded-lg font-bold text-lg transition transform hover:scale-[1.02] shadow-lg disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                  style={{
                    background:
                      'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                  }}
                >
                  {isSubmitting ? 'Bezig met versturen...' : 'Aanvraag indienen'}
                </button>
              </form>
            </div>
          </section>

          {/* FAQ */}
          <section id="faq" className="scroll-mt-20">
            <h2 className="text-3xl font-bold text-blue-900 mb-8 text-center">Veelgestelde vragen</h2>
            <div className="space-y-4 max-w-3xl mx-auto">
              {faq.map((item) => (
                <details
                  key={item.question}
                  className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden group"
                >
                  <summary className="cursor-pointer p-5 font-semibold text-blue-900 hover:bg-orange-50 transition flex items-center justify-between">
                    <span>{item.question}</span>
                    <span className="text-orange-500 group-open:rotate-45 transition-transform text-xl ml-4">
                      +
                    </span>
                  </summary>
                  <div className="px-5 pb-5 text-gray-700 leading-relaxed">{item.answer}</div>
                </details>
              ))}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default IdeeTester;
